import type { MouseEventHandler } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Circle,
  Code2,
  Library,
} from "lucide-react";
import type { ModuleMeta } from "../../content/catalog";
import type { ProgressData } from "../../store/progress";
import { lessonStatus } from "../../lib/lessonStatus";

export default function CourseDirectory({
  modules,
  progress,
  search,
  setSearch,
  onNavigate,
}: {
  modules: readonly ModuleMeta[];
  progress: ProgressData;
  search: string;
  setSearch: (value: string) => void;
  onNavigate: MouseEventHandler<HTMLAnchorElement>;
}) {
  const location = useLocation();
  return (
    <>
      <a className="brand" href="/">
        <span className="brand-mark">zh</span>
        <span>
          <strong>张浩学习小站</strong>
          <small>A LITTLE BETTER, EVERY DAY</small>
        </span>
      </a>
      <details className="space-switch">
        <summary>
          <Code2 size={17} />
          前端学习
          <ChevronDown size={15} />
        </summary>
        <nav aria-label="全站导航">
          <a href="/">
            概览
            <ArrowUpRight size={13} />
          </a>
          <a href="/webtts/">TTS 听说</a>
          <a href="/workflow/">工作流</a>
          <a href="/library">图书馆</a>
        </nav>
      </details>
      <div className="course-search">
        <input
          aria-label="搜索课程"
          placeholder="搜索知识点…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <kbd>⌕</kbd>
      </div>
      <p className="nav-eyebrow">我的学习路径</p>
      <nav className="lesson-tree" aria-label="课程目录">
        {modules.map((module) => (
          <details key={module.id} open>
            <summary>
              <span>{String(module.order).padStart(2, "0")}</span>
              {module.title}
              <ChevronDown size={13} />
            </summary>
            {module.lessons
              .filter((lesson) =>
                `${lesson.title} ${lesson.objective} ${lesson.keyPoints.join(" ")}`
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
              .map((lesson) => {
                const status = lessonStatus(
                  lesson.id,
                  module.quiz.id,
                  progress,
                  module.quiz.passScorePercent,
                );
                return (
                  <Link
                    key={lesson.id}
                    to={`/learn/${lesson.id}`}
                    onClick={onNavigate}
                    aria-labelledby={`directory-title-${lesson.id}`}
                    aria-describedby={`directory-status-${lesson.id}`}
                    aria-current={
                      location.pathname === `/learn/${lesson.id}`
                        ? "page"
                        : undefined
                    }
                  >
                    {status === "已完成" ? (
                      <CheckCircle2 size={14} aria-hidden="true" />
                    ) : (
                      <Circle size={11} aria-hidden="true" />
                    )}
                    <span className="directory-lesson-label">
                      <span id={`directory-title-${lesson.id}`}>
                        {lesson.title}
                      </span>
                      <small id={`directory-status-${lesson.id}`}>
                        {status}
                      </small>
                    </span>
                  </Link>
                );
              })}
            <Link
              className="module-quiz"
              to={`/quiz/${module.id}`}
              onClick={onNavigate}
            >
              ↗ 阶段测评
            </Link>
          </details>
        ))}
      </nav>
      <div className="sidebar-bottom">
        <p>
          把理解，
          <br />
          写成看得见的作品。
        </p>
        <Link to="/profile" onClick={onNavigate}>
          <Activity size={15} />
          学习记录
        </Link>
        <Link to="/mistakes" onClick={onNavigate}>
          <BookOpen size={15} />
          错题本
        </Link>
        <a href="/library">
          <Library size={15} />
          图书馆
        </a>
        <small>本地保存 · 无需登录</small>
      </div>
    </>
  );
}
